import { Box, FormControl, FormLabel, FormHelperText, Typography } from '@mui/material'
import { styled, SxProps } from '@mui/material/styles'
import { ReactNode } from 'react'

interface StyledProps {
  inline?: boolean
  divider?: boolean
  labelWidth?: number
}

const Description = styled('p')(({ theme }) => ({
  fontWeight: 400,
  fontSize: '0.75rem',
  color: theme.palette.grey[500],
  margin: 0,
  marginTop: '10.5px',
}))

const MyFormControl = styled(FormControl, {
  shouldForwardProp: (prop) => prop !== 'inline',
})<StyledProps>(({ theme, inline, divider }) => ({
  display: 'flex',
  flexDirection: inline ? 'row' : 'column',
  marginBottom: theme.spacing(2),
  paddingBottom: divider ? theme.spacing(2) : 0,
  borderBottom: divider ? `1px solid ${theme.palette.divider}` : 'none',
  '&:last-of-type': {
    marginBottom: 0,
    borderBottom: 'none',
  },
}))

const MyFormLabel = styled(FormLabel, {
  shouldForwardProp: (prop) => prop !== 'inline' && prop !== 'labelWidth',
})<StyledProps>(({ theme, inline, labelWidth }) => ({
  display: 'inline-block',
  flexShrink: 0,
  width: labelWidth || 'auto',
  margin: inline ? theme.spacing(1, 2, 1, 0) : theme.spacing(0, 0, 1, 0),
  color: theme.palette.common.black,
  lineHeight: '1.1876em', // baseInput line-height
  fontWeight: theme.typography.fontWeightMedium,
  textAlign: 'left',
}))

export interface FieldProps extends StyledProps {
  label?: ReactNode
  description?: ReactNode
  labelFor?: string
  error?: string
  required?: boolean
  children?: ReactNode
  sx?: SxProps
}

const Field = ({
  label,
  description,
  labelFor,
  labelWidth,
  error,
  inline,
  divider,
  required,
  children,
  sx,
}: FieldProps) => {
  return (
    <MyFormControl {...{ sx }} error={Boolean(error)} inline={inline} divider={divider}>
      {label && (
        <MyFormLabel htmlFor={labelFor} inline={inline} labelWidth={labelWidth}>
          <span>
            {label}
            {required && (
              <Typography display="inline" color="error">
                {' '}
                *
              </Typography>
            )}
          </span>
          {description && <Description>{description}</Description>}
        </MyFormLabel>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', width: 1 }}>
        {children}
        {error && (
          <FormHelperText error variant="outlined">
            {error}
          </FormHelperText>
        )}
      </Box>
    </MyFormControl>
  )
}

export default Field
